import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, KeyRound, Copy, Check, Smartphone, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

// Safe clipboard copy — falls back to a hidden <textarea> + execCommand
// because the Clipboard API is blocked inside cross-origin preview iframes.
const safeCopy = (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
      return true;
    }
  } catch (_) { /* ignore */ }
  return fallbackCopy(text);
};
const fallbackCopy = (text) => {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch (_) { return false; }
};

export default function TwoFactorPage() {
  const [status, setStatus] = useState({ enabled: false, backup_remaining: 0, loading: true });
  const [setupData, setSetupData] = useState(null);
  const [step, setStep] = useState("idle");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const reload = async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/profile/2fa/status");
      setStatus({ ...data, loading: false });
    } catch {
      setStatus({ enabled: false, backup_remaining: 0, loading: false });
    }
  };
  useEffect(() => { reload(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/profile/2fa/setup");
      setSetupData(data);
      setStep("scan");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to start 2FA setup");
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit code from your authenticator");
    if (!password) return toast.error("Enter your account password");
    setBusy(true);
    try {
      await api.post("/profile/2fa/enable", {
        password, secret: setupData.secret,
        totp_code: otp,
        backup_codes: setupData.backup_codes,
      });
      toast.success("Two-factor authentication enabled");
      setStep("backup");
      setOtp(""); setPassword("");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to enable 2FA");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!password) return toast.error("Enter your account password");
    if (otp.length !== 6) return toast.error("Enter the 6-digit code from your authenticator");
    setBusy(true);
    try {
      await api.post("/profile/2fa/disable", { password, totp_code: otp });
      toast.success("Two-factor authentication disabled");
      setSetupData(null); setStep("idle"); setOtp(""); setPassword("");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to disable 2FA");
    } finally {
      setBusy(false);
    }
  };

  const regenBackup = async () => {
    if (!password) return toast.error("Enter your account password");
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data } = await api.post("/profile/2fa/regenerate-backup-codes", { password, totp_code: otp });
      setSetupData({ ...(setupData || {}), backup_codes: data.backup_codes });
      setStep("backup");
      setOtp(""); setPassword("");
      toast.success("New backup codes generated");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to regenerate codes");
    } finally {
      setBusy(false);
    }
  };

  const copy = (txt, key) => {
    if (safeCopy(txt)) {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } else {
      toast.error("Copy not allowed in this preview — please copy the text manually");
    }
  };

  return (
    <ProfileShell title="Two-factor authentication">
      <div className="py-3 pb-10 space-y-5">
        {/* Hero header */}
        <div
          className="relative overflow-hidden rounded-2xl border border-white/10 p-5 sm:p-6"
          style={{
            background:
              "radial-gradient(900px 280px at 0% 0%, rgba(25,165,92,0.18), transparent 60%)," +
              "radial-gradient(700px 240px at 100% 100%, rgba(245,158,11,0.10), transparent 60%)," +
              "linear-gradient(180deg,#0d1422 0%,#0a1018 100%)",
          }}
        >
          <div className="relative flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl grid place-items-center"
              style={{ background: "linear-gradient(135deg,#34d399 0%,#10b981 60%,#059669 100%)", boxShadow: "0 12px 30px -8px rgba(16,185,129,0.55)" }}>
              <ShieldCheck size={26} className="text-[#06140b]" strokeWidth={2.4} />
            </div>
            <div className="flex-1">
              <div className="text-[10.5px] uppercase tracking-[2px] font-bold text-emerald-300/85">Two-factor</div>
              <div className="text-xl sm:text-2xl font-black tracking-tight mt-0.5">Add an extra layer</div>
              <div className="text-[12px] text-white/55 mt-0.5">Protect your account with a 6-digit code at every login.</div>
            </div>
          </div>
        </div>

        {/* 2FA card */}
        <div className="rounded-2xl bg-[#0d1422]/85 border border-white/[0.07] p-5 sm:p-6 space-y-4 backdrop-blur-xl" data-testid="profile-2fa-section">
          <div className="flex items-center gap-3 pb-3 border-b border-white/[0.06]">
            <div className={`h-9 w-9 rounded-lg grid place-items-center border ${
              status.enabled
                ? "bg-emerald-500/12 border-emerald-500/30 text-emerald-300"
                : "bg-amber-500/12 border-amber-500/30 text-amber-300"
            }`}>
              {status.enabled ? <ShieldCheck size={16} /> : <ShieldAlert size={16} />}
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-bold tracking-tight">Two-factor authentication</div>
              <div className="text-[11.5px] text-white/45">Google Authenticator (TOTP) — a 6-digit code at every login.</div>
            </div>
            {!status.loading && (
              <span className={`text-[10px] uppercase tracking-[1.5px] font-bold px-2.5 py-1 rounded-full border ${
                status.enabled
                  ? "bg-emerald-500/12 text-emerald-300 border-emerald-500/30"
                  : "bg-amber-500/12 text-amber-300 border-amber-500/30"
              }`}>
                {status.enabled ? "Enabled" : "Off"}
              </span>
            )}
          </div>

          {step === "idle" && !status.enabled && (
            <div className="space-y-4">
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3.5 flex gap-3 items-start">
                <ShieldAlert size={16} className="text-amber-300 mt-0.5 shrink-0" />
                <div className="text-[12.5px] text-amber-200/95 leading-relaxed">
                  Your account is currently <b>not protected</b> by 2FA. Enable it now — it takes ~30 seconds and you only need an authenticator app on your phone.
                </div>
              </div>
              <button
                onClick={startSetup}
                disabled={busy}
                className="group relative w-full h-12 rounded-xl text-[#06140b] font-extrabold inline-flex items-center justify-center gap-2 overflow-hidden disabled:opacity-60 transition-all"
                style={{
                  background: "linear-gradient(135deg,#34d399 0%,#10b981 50%,#059669 100%)",
                  boxShadow: "0 12px 30px -10px rgba(16,185,129,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                }}
                data-testid="2fa-start-setup"
              >
                <Smartphone size={16} className="relative" />
                <span className="relative">{busy ? "Loading…" : "Set up Google Authenticator"}</span>
              </button>
            </div>
          )}

          {step === "idle" && status.enabled && (
            <div className="space-y-3.5">
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3.5 flex gap-3 items-start">
                <ShieldCheck size={16} className="text-emerald-300 mt-0.5 shrink-0" />
                <div className="text-[12.5px] text-emerald-200/95 leading-relaxed">
                  <b>Two-factor authentication is active.</b> You'll be asked for a 6-digit code at every login. <b>{status.backup_remaining}</b> backup codes remaining.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setStep("regen")} className="h-10 px-3.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-[12.5px] font-semibold text-white/85 inline-flex items-center gap-1.5 transition" data-testid="2fa-regen">
                  <RotateCcw size={13} /> Regenerate backup codes
                </button>
                <button onClick={() => setStep("disable")} className="h-10 px-3.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-[12.5px] font-bold text-rose-300 inline-flex items-center gap-1.5 transition" data-testid="2fa-disable-start">
                  <ShieldAlert size={13} /> Disable 2FA
                </button>
              </div>
            </div>
          )}

          {step === "scan" && setupData && (
            <div className="space-y-4">
              <div className="bg-[#0a1018] rounded-lg p-4 flex flex-col sm:flex-row items-center gap-4 border border-white/5">
                <img src={setupData.qr_data_url} alt="2FA QR" className="h-44 w-44 rounded-md bg-white p-1" />
                <div className="text-xs text-white/65 space-y-2 flex-1">
                  <p className="font-semibold text-white/85">1. Scan with your authenticator app</p>
                  <p className="text-[11px]">Or enter the secret manually:</p>
                  <div className="bg-black/40 border border-white/10 rounded px-2 py-1.5 font-mono text-[12px] text-white/85 break-all flex items-center gap-2">
                    <span className="flex-1">{setupData.secret}</span>
                    <button type="button" onClick={() => copy(setupData.secret, "sec")} className="text-emerald-300 hover:text-emerald-200">
                      {copied === "sec" ? <Check size={13} /> : <Copy size={13} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-white/65 font-semibold">2. Verify by entering your password and 6-digit code</p>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Account password"
                  className="w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-white placeholder:text-white/30 focus:border-emerald-500 focus:outline-none"
                  data-testid="2fa-enable-password" />
                <input type="text" inputMode="numeric" pattern="[0-9]*" value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000" maxLength={6}
                  className="w-full h-12 rounded-lg bg-[#101824] border border-white/10 px-3 text-center text-2xl tracking-[0.4em] font-mono text-white focus:border-emerald-500 focus:outline-none"
                  data-testid="2fa-enable-code" />
              </div>
              <div className="flex gap-2">
                <button onClick={enable} disabled={busy || otp.length !== 6 || !password}
                  className="flex-1 h-11 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-60"
                  data-testid="2fa-enable-confirm">
                  {busy ? "Enabling…" : "Verify & enable"}
                </button>
                <button onClick={() => { setStep("idle"); setSetupData(null); setOtp(""); setPassword(""); }}
                  className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-sm text-white/70">Cancel</button>
              </div>
            </div>
          )}

          {step === "backup" && setupData?.backup_codes && (
            <div className="space-y-3">
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-[12px] text-amber-200 flex gap-2 items-start">
                <KeyRound size={14} className="mt-0.5 shrink-0" />
                <span><b>Save these backup codes in a safe place.</b> Each can only be used once if you lose access to your authenticator. They will not be shown again.</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {setupData.backup_codes.map((c) => (
                  <div key={c} className="bg-[#0a1018] border border-white/10 rounded px-2.5 py-2 font-mono text-[13px] text-white/90 flex items-center gap-1.5">
                    <span className="flex-1">{c}</span>
                    <button onClick={() => copy(c, c)} className="text-emerald-300/70 hover:text-emerald-200">
                      {copied === c ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => copy(setupData.backup_codes.join("\n"), "all")} className="h-10 px-3 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/85 inline-flex items-center gap-1.5">
                  {copied === "all" ? <Check size={12} /> : <Copy size={12} />} Copy all
                </button>
                <button onClick={() => { setStep("idle"); setSetupData(null); }}
                  className="flex-1 h-10 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold" data-testid="2fa-backup-done">
                  I've saved them
                </button>
              </div>
            </div>
          )}

          {step === "disable" && (
            <div className="space-y-3">
              <p className="text-xs text-rose-300">Confirm your password and current 6-digit code to disable 2FA.</p>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Account password"
                className="w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-white" data-testid="2fa-disable-password" />
              <input type="text" inputMode="numeric" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000" maxLength={6}
                className="w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-center text-xl tracking-[0.4em] font-mono text-white" data-testid="2fa-disable-code" />
              <div className="flex gap-2">
                <button onClick={disable} disabled={busy || otp.length !== 6 || !password}
                  className="flex-1 h-11 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold disabled:opacity-60" data-testid="2fa-disable-confirm">
                  {busy ? "Disabling…" : "Disable 2FA"}
                </button>
                <button onClick={() => { setStep("idle"); setOtp(""); setPassword(""); }}
                  className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-sm text-white/70">Cancel</button>
              </div>
            </div>
          )}

          {step === "regen" && (
            <div className="space-y-3">
              <p className="text-xs text-white/65">Regenerating will invalidate all current backup codes. Confirm with your password and a fresh 6-digit code.</p>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Account password"
                className="w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-white" />
              <input type="text" inputMode="numeric" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000" maxLength={6}
                className="w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-center text-xl tracking-[0.4em] font-mono text-white" />
              <div className="flex gap-2">
                <button onClick={regenBackup} disabled={busy || otp.length !== 6 || !password}
                  className="flex-1 h-11 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-60">
                  {busy ? "Generating…" : "Generate new codes"}
                </button>
                <button onClick={() => { setStep("idle"); setOtp(""); setPassword(""); }}
                  className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-sm text-white/70">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ProfileShell>
  );
}
